import { PropsWithChildren, useEffect, useRef, useState } from "react";

import { logAnalyticsEvent, unlockAnalyticsQueue } from "@/constants/amplitude";
import { EventLoadStatus, WebEventProps, WebEventTypes } from "@/constants/event";
import { localStorageManager, LocalStorageManagerKeys } from "@/utils/localStorageManager";

/**
 * Provider to track user's first funnel entry
 * Ensures FUNNEL_ENTRY is sent first by queuing all other events until DOM is loaded
 * Uses the built-in queue mechanism in Amplitude trackers
 */
const FirstFunnelEntryProvider = ({ children }: PropsWithChildren) => {
    const hasTrackedRef = useRef(false);
    const [isDOMReady, setIsDOMReady] = useState(false);

    // Check if DOM is ready and visible
    useEffect(() => {
        const checkDOMReady = () => {
            if (document.readyState === "complete" || document.readyState === "interactive") {
                setIsDOMReady(true);
            }
        };

        // Check immediately
        checkDOMReady();

        // Listen for DOM ready event
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", checkDOMReady);
            window.addEventListener("load", checkDOMReady);

            return () => {
                document.removeEventListener("DOMContentLoaded", checkDOMReady);
                window.removeEventListener("load", checkDOMReady);
            };
        }
    }, []);

    // Send FUNNEL_ENTRY event and unlock queue when DOM is ready
    useEffect(() => {
        if (!isDOMReady || hasTrackedRef.current) {
            return;
        }

        // Check if this is the first visit (i.e., not yet tracked)
        const isFirstVisit = !localStorageManager.getStoredItem<boolean>(
            LocalStorageManagerKeys.IS_FIRST_VISIT_TRACKED
        );

        if (isFirstVisit) {
            // Unlock trackers WITHOUT flushing the queue yet
            unlockAnalyticsQueue(false);

            // Send FUNNEL_ENTRY (will go through immediately since unlocked)
            logAnalyticsEvent(WebEventTypes.FUNNEL_ENTRY, {
                [WebEventProps.LOAD_STATUS]: EventLoadStatus.SUCCESS,
            });

            // Mark first visit as tracked in localStorage
            localStorageManager.storeItem(LocalStorageManagerKeys.IS_FIRST_VISIT_TRACKED, true);

            // Now flush the queued events
            setTimeout(() => {
                unlockAnalyticsQueue();
            }, 10);
        } else {
            // Not first visit, just unlock and flush the queue
            unlockAnalyticsQueue();
        }

        // Mark as tracked
        hasTrackedRef.current = true;
    }, [isDOMReady]);

    return <>{children}</>;
};

export default FirstFunnelEntryProvider;
